/**
 * Composant réutilisable d'instructions d'installation PWA
 *
 * Affiche les instructions adaptées selon la plateforme détectée.
 * Utilisé à la fois dans le prompt contextuel et dans la page des paramètres.
 *
 * @module InstallInstructions
 * @author Équipe Brydge
 * @since 1.0.0
 * @requires @mui/material
 */

import {Box, Card, Stack, Typography, Button, Alert, Divider} from '@mui/material';
import {
  GetApp as InstallIcon,
  IosShare as IosShareIcon,
  AddBox as AddBoxIcon,
  MoreHoriz as MoreHorizIcon,
  CheckCircle as CheckIcon,
  Smartphone as SmartphoneIcon,
  Computer as ComputerIcon,
} from '@mui/icons-material';
import {detectPlatform, isStandalone} from '../utils/detectPlatform';

/**
 * Composant d'instructions d'installation PWA
 *
 * @param {Object} props - Props du composant
 * @param {Object|null} props.deferredPrompt - Événement beforeinstallprompt capturé
 * @param {Function} props.onInstall - Callback appelé quand l'utilisateur clique sur "Installer"
 * @param {boolean} [props.showBenefits=false] - Afficher la section des avantages
 * @param {boolean} [props.compact=false] - Mode compact pour le prompt flottant
 * @return {JSX.Element} Instructions d'installation adaptées
 */
export default function InstallInstructions({
  deferredPrompt,
  onInstall,
  showBenefits = false,
  compact = false,
}) {
  const detectedPlatform = detectPlatform();

  // App déjà installée
  if (isStandalone()) {
    return (
      <Alert severity="success" icon={<CheckIcon />}>
        <Typography variant="body2" fontWeight={600} gutterBottom>
          Application installée
        </Typography>
        <Typography variant="body2">
          L&apos;application Brydge est déjà installée sur votre appareil. Vous pouvez la lancer
          depuis votre écran d&apos;accueil.
        </Typography>
      </Alert>
    );
  }

  // iOS
  if (detectedPlatform === 'ios') {
    return (
      <Stack spacing={compact ? 2 : 3}>
        {!compact && (
          <Alert severity="info" icon={<SmartphoneIcon />}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Installation sur iOS/Safari
            </Typography>
            <Typography variant="body2">
              Suivez les étapes ci-dessous pour installer Brydge sur votre iPhone ou iPad.
            </Typography>
          </Alert>
        )}

        <Card sx={{p: compact ? 2 : 3, bgcolor: 'background.neutral'}}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '0.875rem',
                  flexShrink: 0,
                }}
              >
                1
              </Box>
              <Stack spacing={0.5} sx={{flex: 1, minWidth: 0}}>
                <Typography variant="subtitle2">Ouvrir le menu de partage</Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2" color="text.secondary">
                    Appuyez sur
                  </Typography>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 28,
                      height: 28,
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                    }}
                  >
                    <IosShareIcon fontSize="small" color="primary" />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    en bas de Safari
                  </Typography>
                </Stack>
              </Stack>
            </Stack>

            <Divider />

            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '0.875rem',
                  flexShrink: 0,
                }}
              >
                2
              </Box>
              <Stack spacing={0.5} sx={{flex: 1, minWidth: 0}}>
                <Typography variant="subtitle2">Ajouter à l&apos;écran d&apos;accueil</Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2" color="text.secondary">
                    Sélectionnez
                  </Typography>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 1,
                      height: 28,
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                    }}
                  >
                    <AddBoxIcon fontSize="small" color="primary" sx={{mr: 0.5}} />
                    <Typography variant="caption" fontWeight={600}>
                      Sur l&apos;écran d&apos;accueil
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Stack>

            <Divider />

            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '0.875rem',
                  flexShrink: 0,
                }}
              >
                3
              </Box>
              <Stack spacing={0.5} sx={{flex: 1, minWidth: 0}}>
                <Typography variant="subtitle2">Confirmer l&apos;installation</Typography>
                <Typography variant="body2" color="text.secondary">
                  Appuyez sur <strong>&quot;Ajouter&quot;</strong> pour installer l&apos;application
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Card>
      </Stack>
    );
  }

  // Android/Desktop avec deferredPrompt disponible
  if (deferredPrompt) {
    return (
      <Stack spacing={compact ? 2 : 3}>
        {!compact && (
          <Alert severity="success" icon={<CheckIcon />}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Installation disponible
            </Typography>
            <Typography variant="body2">
              Votre navigateur prend en charge l&apos;installation. Cliquez sur le bouton
              ci-dessous.
            </Typography>
          </Alert>
        )}

        <Box textAlign="center">
          <Button
            variant="contained"
            size={compact ? 'medium' : 'large'}
            startIcon={<InstallIcon />}
            onClick={onInstall}
            sx={{
              px: compact ? 3 : 4,
              py: compact ? 1 : 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: compact ? '0.9375rem' : '1rem',
              fontWeight: 600,
            }}
          >
            Installer maintenant
          </Button>
        </Box>
      </Stack>
    );
  }

  // Android/Desktop sans deferredPrompt
  return (
    <Stack spacing={compact ? 2 : 3}>
      {!compact && (
        <Alert
          severity="info"
          icon={detectedPlatform === 'android' ? <SmartphoneIcon /> : <ComputerIcon />}
        >
          <Typography variant="body2" fontWeight={600} gutterBottom>
            Installation sur {detectedPlatform === 'android' ? 'Android/Chrome' : 'Chrome Desktop'}
          </Typography>
          <Typography variant="body2">
            Utilisez le menu de votre navigateur Chrome pour installer.
          </Typography>
        </Alert>
      )}

      <Card sx={{p: compact ? 2 : 3, bgcolor: 'background.neutral'}}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: '0.875rem',
                flexShrink: 0,
              }}
            >
              1
            </Box>
            <Stack spacing={0.5} sx={{flex: 1, minWidth: 0}}>
              <Typography variant="subtitle2">Ouvrir le menu Chrome</Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="body2" color="text.secondary">
                  Appuyez sur
                </Typography>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                  }}
                >
                  <MoreHorizIcon fontSize="small" color="primary" />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  (trois points)
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          <Divider />

          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: '0.875rem',
                flexShrink: 0,
              }}
            >
              2
            </Box>
            <Stack spacing={0.5} sx={{flex: 1, minWidth: 0}}>
              <Typography variant="subtitle2">Installer l&apos;application</Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="body2" color="text.secondary">
                  Sélectionnez
                </Typography>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 1,
                    height: 28,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                  }}
                >
                  <InstallIcon fontSize="small" color="primary" sx={{mr: 0.5}} />
                  <Typography variant="caption" fontWeight={600}>
                    Installer l&apos;application
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Stack>

          <Divider />

          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: '0.875rem',
                flexShrink: 0,
              }}
            >
              3
            </Box>
            <Stack spacing={0.5} sx={{flex: 1, minWidth: 0}}>
              <Typography variant="subtitle2">Confirmer l&apos;installation</Typography>
              <Typography variant="body2" color="text.secondary">
                Appuyez sur <strong>&quot;Installer&quot;</strong> pour confirmer
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
}
